<template>
    <portal to="overlay" :disabled="!usePortal">
        <div
            class="Modal"
            ref="modal"
            :class="[{ isActive: isActive, 'is-fullscreen': fullscreen }]"
        >
            <div
                class="Modal-Content"
                :class="[
                    `is-${size}`,
                    {
                        'is-feature-announcement': featureAnnouncementLayout,
                        'is-fullscreen': fullscreen,
                    },
                ]"
            >
                <div class="Modal-Header" v-if="!featureAnnouncementLayout && !fullscreen">
                    <div class="header-content">
                        <h3 v-if="title">{{ title }}</h3>
                        <slot name="header"></slot>
                    </div>
                    <button class="close-button" @click="closeModal">
                        <i class="ri-close-line"></i>
                    </button>
                </div>
                <div
                    v-if="fullscreen && !featureAnnouncementLayout"
                    class="Modal-Header--fullscreen"
                >
                    <button class="close-button" @click="closeModal">
                        <i class="ri-close-line"></i>
                    </button>
                </div>
                <div v-if="featureAnnouncementLayout" class="Modal-Header--minimal">
                    <button class="close-button" @click="closeModal">
                        <i class="ri-close-line"></i>
                    </button>
                </div>
                <div v-if="featureAnnouncementLayout" class="Modal-Media">
                    <slot name="media"></slot>
                </div>
                <div
                    class="Modal-Body"
                    :class="{ 'is-feature-announcement': featureAnnouncementLayout }"
                >
                    <div v-if="featureAnnouncementLayout" class="Modal-TitleSection">
                        <slot name="header">
                            <h3 v-if="title">{{ title }}</h3>
                        </slot>
                    </div>
                    <p v-if="text">{{ text }}</p>
                    <slot></slot>
                </div>
                <div class="Modal-Actions" v-if="hasActions">
                    <slot name="actions">
                        <Button
                            v-if="cancel.text"
                            tabindex="0"
                            variant="basic"
                            :class="`is-${cancel.color}`"
                            @onClick="handleAction(cancel.callback)"
                        >
                            {{ cancel.text }}
                        </Button>
                        <Button
                            v-if="action.text"
                            tabindex="0"
                            variant="success"
                            :isLoading="isLoading || action.isLoading"
                            :class="`is-${action.color}`"
                            @onClick="handlePositiveActionClick(action.callback)"
                        >
                            {{ action.text }}
                        </Button>
                    </slot>
                </div>
            </div>
            <div class="Backdrop" @click="closeModalOnBackdropClick" />
        </div>
    </portal>
</template>

<script>
import Button from '@/components/widgets/Button';
import { Portal } from 'portal-vue';
import { gsap } from 'gsap';

const modalStack = [];

export default {
    components: {
        Button,
        Portal,
    },
    name: 'Modal',
    props: {
        animateHeight: {
            type: Boolean,
            default: true,
        },
        closeOnBackdropClick: {
            type: Boolean,
            default: true,
        },

        show: Boolean,
        isLoading: Boolean,
        title: String,
        text: String,
        cancel: {
            type: Object,
            default: () => ({
                text: '',
                color: '',
                callback: () => {},
            }),
        },
        action: {
            type: Object,
            default: () => ({
                text: '',
                color: '',
                callback: () => {},
            }),
        },
        size: {
            type: String,
            default: 'small',
        },
        featureAnnouncementLayout: {
            type: Boolean,
            default: false,
        },
        fullscreen: {
            type: Boolean,
            default: false,
        },
        usePortal: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        actionTextDefault: 'OK',
        cancelTextDefault: 'Cancel',
        isActive: false,
        resizeObserver: null,
        lastHeight: null,
        isAnimating: false,
    }),
    computed: {
        hasActions() {
            return this.$slots.actions || this.cancel.text || this.action.text;
        },
    },
    watch: {
        show: {
            handler(newVal) {
                this.isActive = newVal;
                if (newVal) {
                    this.registerToStack();
                    this.$nextTick(() => {
                        this.animateModalIn();
                        this.setupResizeObserver();
                        setTimeout(() => {
                            const input = this.$el.querySelector('input');
                            if (input) {
                                input.focus();
                            }
                        }, 100);
                    });
                } else {
                    this.unregisterFromStack();
                    this.destroyResizeObserver();
                    this.lastHeight = null;
                    this.animateModalOut();
                    this.$nextTick(() => {
                        if (this.$refs.modal) {
                            const overlay = this.$refs.modal.querySelector('.Backdrop');
                            if (overlay) {
                                gsap.set(overlay, {
                                    opacity: 0,
                                    force3D: true,
                                });
                            }
                        }
                    });
                }
            },
            immediate: true,
        },
    },
    methods: {
        closeModalOnBackdropClick() {
            if (this.closeOnBackdropClick) {
                this.closeModal();
            }
        },
        async closeModal() {
            await this.animateModalOut();
            this.$emit('onModalClose');
        },
        async handlePositiveActionClick(cb) {
            try {
                cb();
            } catch (e) {}
        },
        handleAction(cb) {
            try {
                cb();
            } catch (e) {}
            if (!cb) {
                this.closeModal();
            }
        },
        animateModalIn() {
            if (!this.$refs.modal) return;

            const content = this.$refs.modal.querySelector('.Modal-Content');
            const overlay = this.$refs.modal.querySelector('.Backdrop');
            const closeButton = this.$refs.modal.querySelector('.close-button');

            if (!content || !overlay) return;

            gsap.set(content, {
                scale: 0.9,
                opacity: 0,
                y: 20,
                force3D: true,
                transformOrigin: 'center center',
            });
            gsap.set(overlay, {
                opacity: 0,
                force3D: true,
            });

            const timeline = gsap.timeline({
                onComplete: () => {
                    this.$emit('opened');
                },
                smoothChildTiming: true,
                autoRemoveChildren: true,
            });

            timeline
                .to(
                    overlay,
                    {
                        opacity: 1,
                        duration: 0.25,
                        ease: 'power2.out',
                        force3D: true,
                    },
                    0,
                )
                .to(
                    content,
                    {
                        scale: 1,
                        opacity: 1,
                        y: 0,
                        duration: 0.35,
                        ease: 'power2.out',
                        force3D: true,
                    },
                    0.05,
                )
                .fromTo(
                    closeButton,
                    {
                        scale: 0,
                        rotation: -90,
                        force3D: true,
                    },
                    {
                        scale: 1,
                        rotation: 0,
                        duration: 0.25,
                        ease: 'back.out(1.4)',
                        force3D: true,
                    },
                    0.15,
                );
        },

        animateModalOut() {
            return new Promise((resolve) => {
                if (!this.$refs.modal) {
                    resolve();
                    return;
                }

                const content = this.$refs.modal.querySelector('.Modal-Content');
                const overlay = this.$refs.modal.querySelector('.Backdrop');
                const closeButton = this.$refs.modal.querySelector('.close-button');

                if (!content || !overlay) {
                    resolve();
                    return;
                }

                const timeline = gsap.timeline({
                    onComplete: () => {
                        this.$emit('closed');
                        resolve();
                    },
                    smoothChildTiming: true,
                    autoRemoveChildren: true,
                });

                timeline
                    .to(
                        closeButton,
                        {
                            scale: 0,
                            rotation: 90,
                            duration: 0.15,
                            ease: 'power2.in',
                            force3D: true,
                        },
                        0,
                    )
                    .to(
                        content,
                        {
                            scale: 0.9,
                            opacity: 0,
                            y: 20,
                            duration: 0.25,
                            ease: 'power2.in',
                            force3D: true,
                        },
                        0.05,
                    )
                    .to(
                        overlay,
                        {
                            opacity: 0,
                            duration: 0.2,
                            ease: 'power2.in',
                            force3D: true,
                        },
                        0.1,
                    );
            });
        },

        setupResizeObserver() {
            if (!this.animateHeight || this.resizeObserver) return;

            const body = this.$refs.modal?.querySelector('.Modal-Body');
            if (!body) return;

            this.resizeObserver = new ResizeObserver((entries) => {
                if (!this.isActive || this.isAnimating) return;

                const content = this.$refs.modal?.querySelector('.Modal-Content');
                if (!content) return;

                const newHeight = content.scrollHeight;

                if (this.lastHeight !== null && this.lastHeight !== newHeight) {
                    this.animateHeightTo(content, this.lastHeight, newHeight);
                }

                this.lastHeight = newHeight;
            });

            this.resizeObserver.observe(body);
        },

        destroyResizeObserver() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }
        },

        animateHeightTo(element, fromHeight, toHeight) {
            this.isAnimating = true;

            gsap.fromTo(
                element,
                { height: fromHeight },
                {
                    height: toHeight,
                    duration: 0.3,
                    ease: 'power2.out',
                    onComplete: () => {
                        element.style.height = '';
                        this.isAnimating = false;
                    },
                },
            );
        },

        registerToStack() {
            if (!modalStack.includes(this)) {
                modalStack.push(this);
            }
        },

        unregisterFromStack() {
            const index = modalStack.indexOf(this);
            if (index > -1) {
                modalStack.splice(index, 1);
            }
        },
    },
    mounted: function () {
        this.$nextTick(() => {
            if (this.$refs.modal && !this.isActive) {
                const overlay = this.$refs.modal.querySelector('.Backdrop');
                if (overlay) {
                    gsap.set(overlay, {
                        opacity: 0,
                        force3D: true,
                    });
                }
            }
        });

        this.handleKeydown = (e) => {
            if (e.keyCode === 27 && this.show) {
                if (modalStack.length > 0 && modalStack[modalStack.length - 1] === this) {
                    e.stopImmediatePropagation();
                    this.closeModal();
                }
            }
        };

        document.addEventListener('keydown', this.handleKeydown);

        if (this.show) {
            this.registerToStack();
        }
    },
    beforeDestroy() {
        this.destroyResizeObserver();
        this.unregisterFromStack();
        if (this.handleKeydown) {
            document.removeEventListener('keydown', this.handleKeydown);
        }
    },
};
</script>

<style lang="scss" >
.theme-dark {
    .Backdrop {
        background-color: rgba(0, 0, 0, 1);
    }

    .Modal-Content {
        border: solid 0.5px var(--color-text-inactive);
    }
}

.Modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 10000;
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0;
    will-change: contents;
    visibility: hidden;
    perspective: 800px;
    perspective-origin: center center;
    padding: 80px;

    @media screen and (max-width: $breakpoint-tablet-portrait) {
        padding: 10px;
    }

    &.isActive {
        opacity: 1;
        visibility: visible;
    }

    &.is-fullscreen {
        padding: 0;
    }

    .Backdrop {
        background-color: rgba(0, 0, 0, 0.7);
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 0;
    }

    &.is-fullscreen .Backdrop {
        background-color: rgba(0, 0, 0, 0.5);
    }

    .Modal-Content {
        z-index: 1;
        background-color: var(--color-white);
        border-radius: 8px;
        width: 90%;
        max-width: 1200px;
        height: 80%;
        max-height: 800px;
        display: flex;
        flex-direction: column;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);

        &.is-small {
            width: 600px;
            max-width: 90vw;
            height: auto;
            max-height: 70vh;
        }

        &.is-medium {
            min-width: clamp(350px, 60vw, 650px);
            height: auto;
            max-height: 80vh;
        }

        &.is-large {
            min-width: clamp(350px, 90vw, 90vw);
            height: fit-content;
            max-height: 90vh;
        }

        &.is-fullscreen {
            width: 100%;
            max-width: 100%;
            height: 100%;
            max-height: 100%;
            border-radius: 0;
            padding: 0;
            background-color: rgba(240, 241, 247, 1);
        }

        @media screen and (max-width: $breakpoint-tablet-portrait) {
            width: 100%;
            height: auto;
            max-height: 90vh;

            &.is-fullscreen {
                max-height: 100%;
            }
        }
    }

    .Modal-Header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px 24px;
        border-bottom: 0.5px solid #e5e7eb;

        .header-content {
            display: flex;
            align-items: center;
            gap: 12px;
            flex: 1;

            h3 {
                margin: 0;
                font-size: 18px;
                font-weight: 600;
                color: #1f2937;
            }
        }

        .close-button {
            background: none;
            border: none;
            font-size: 20px;
            color: #6b7280;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: all 0.2s;
            flex-shrink: 0;

            &:hover {
                background: #f3f4f6;
                color: #374151;
            }
        }
    }

    .Modal-Body {
        flex: 1;
        overflow-y: auto;
        padding: 20px 24px;

        @media screen and (max-width: $breakpoint-tablet-portrait) {
            padding: 16px;
        }

        p {
            font-size: 1.125rem;
            line-height: 1.5;
            margin-bottom: 16px;

            &:last-child {
                margin-bottom: 0;
            }
        }

        &.is-feature-announcement {
            padding-top: 24px;
        }
    }

    .Modal-Header--fullscreen {
        position: absolute;
        top: 20px;
        right: 20px;
        z-index: 10;

        .close-button {
            background: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(10px);
            border: none;
            font-size: 24px;
            color: #6b7280;
            cursor: pointer;
            padding: 8px;
            border-radius: 50%;
            transition: all 0.2s;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

            &:hover {
                background: rgba(255, 255, 255, 1);
                color: #374151;
                transform: scale(1.05);
            }
        }
    }

    .Modal-Content.is-feature-announcement {
        max-height: calc(100vh - 40px);

        .Modal-Header--minimal {
            position: absolute;
            top: 12px;
            right: 12px;
            z-index: 10;

            .close-button {
                background: rgba(255, 255, 255, 0.9);
                backdrop-filter: blur(10px);
                border: none;
                font-size: 20px;
                color: #6b7280;
                cursor: pointer;
                padding: 8px;
                border-radius: 50%;
                transition: all 0.2s;
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

                &:hover {
                    background: rgba(255, 255, 255, 1);
                    color: #374151;
                    transform: scale(1.05);
                }
            }
        }

        .Modal-Media {
            width: 100%;
            margin: 0;
            height: 100%;
            border-radius: 8px 8px 0 0;

            .VideoPlayer {
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
            }

            :deep(*) {
                width: 100%;
                display: block;
            }
        }

        .Modal-TitleSection {
            margin-bottom: 16px;
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;

            h3 {
                margin: 0;
                font-size: 20px;
                font-weight: 600;
                color: #1f2937;
            }
        }
    }

    .Modal-Actions {
        padding: 20px;
        border-top: 0.5px solid #e5e7eb;
        display: flex;
        justify-content: flex-end;
        gap: 12px;

        @media screen and (max-width: $breakpoint-tablet-portrait) {
            padding: 16px;
        }
    }
}
</style>
